/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { untranslated, type TranslatedString } from "@i18n";
import { startTransition, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import {
  formCategoryObject,
  getPresetSectionsObject,
} from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { optionsViewType } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { formCategories } from "@Helpers";
import {
  addValidationToForms,
  checkMandatoryFields,
  formSectionType,
  getSectionPermission,
  getUserPermissionForForm,
} from "./constants";
import _ from "lodash";
import { PLATFORM, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { SignTypeValues } from "@CubeComponents/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import useFormValidator from "../FormValidation/useFormValidator";
import { getContractDetails } from "@Features/contracts/state/contracts.actions";
import { getBackendUrl } from "@Utils";
import { folderPermissions, presetSectionsCodeObject } from "@Helpers/constants";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import { RootState } from "@Reducers/index";
import type { RecentAccessResponse } from "@ServerTypes/recentAccess/recentAccess.schema";
import type { LocationControllerResponse, TeamsControllerResponse } from "@ServerTypes/projects/project.schema";
import type {
  QuestionField,
  TaskInspectionAndProgressFormsControllerResponse,
  TipfUpdateRequest,
} from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
// `toObjectIdString` re-tags an id that already came out of Mongo (here the session user's account
// `_id`) as the branded `ObjectIdString` the stored cell declares. Its whole body is `String(id)`
// with a nullish short-circuit, so it is a no-op on the strings this file passes it.
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormInstance, FormInstanceFlowLevel, FormInstanceSection } from "../../formInstance.wire";
// The renderers (`questionFields.tsx`) already declare the payload of every handler this hook hands
// them. Those exported types are the contract — reuse them here rather than restating the shapes,
// so the hook and the renderers cannot drift.
import type {
  FieldFileValue,
  FieldModalStateArgs,
  FieldUserValue,
  FileUploadChangeArgs,
  OpenAddUserModalArgs,
  RemoveUploadedFileArgs,
  RemoveUserFromListArgs,
  SaveEditedImageArgs,
} from "./questionFieldsHelper";
// `handleInputChange` is handed straight to `FormQuestionFields`, so its payload is that component's
// declaration of it, read back off the prop rather than restated here.
import type { FormQuestionFieldsProps } from "./questionFields";
import { axiosWrapper } from "@AxiosWrapper";

/*
 * TYPING NOTES FOR THIS FILE
 *
 * 1. `!` after an array index (`template[sectionIndex!]!`) is `noUncheckedIndexedAccess`
 *    bookkeeping, not a claim that the index is always in range. It is erased at emit, so a
 *    missing element still throws exactly where it threw before. Where the *schema* says a field
 *    is optional and the code dereferences it anyway, the assertion carries a `KNOWN BUG` note.
 * 2. `as TranslatedString` on a server-supplied message is the exact erased equivalent of the
 *    `untranslated(...)` marker used elsewhere in this file — that helper's whole body is
 *    `return str as TranslatedString`. Calling it here would emit a call and change the runtime.
 * 3. Casts are repeated inline rather than hoisted into a local: a hoisted `const` is emitted,
 *    a cast is not.
 */

type CustomFieldDataPayload = {
  eachField: QuestionField | undefined;
  answerValue: unknown;
  fieldIndex: number | null | undefined;
  sectionIndex: number | null | undefined;
  tableRowIndex?: number;
  source?: string;
  args?: unknown;
};
type HandleCustomFieldData = (payload: CustomFieldDataPayload) => void;

/**
 * The `handleInputChange` payload, taken from the renderer prop this handler is passed as. It carries
 * `eachField` / `section` as the widest shapes all three of that component's call sites satisfy
 * (`QuestionFieldInput` / `SectionInput` there), which is deliberately looser than the Zod
 * `QuestionField` / `FormInstanceSection` this body reads — so the body asserts them back. `args` is
 * the extra member only the mobile path sends and the renderer does not declare.
 */
type HandleInputChangeArgs = Parameters<NonNullable<FormQuestionFieldsProps["handleInputChange"]>>[0] & {
  args?: unknown;
};

/**
 * Restate `result`'s element on the controller response.
 *
 * The response schema declares `result: z.array(z.unknown())`, and its own comment says why: the
 * element IS `InspectionAndProgressDocument` (forms/inspectionAndProgress.collection.ts), but
 * `forms.schema.ts` cannot import that document back without a module cycle. `FormInstance` is
 * exactly that document's wire shape (`formInstance.wire.ts`), so it is stated once here instead of
 * being re-narrowed at the single read.
 *
 * The `T extends unknown` hop is what keeps the status discriminated union distributed — a plain
 * `Omit` over the union would collapse it and drop `errorMessage` from the failure branch, which
 * several `else` branches below read.
 */
type WithFormInstanceResult<T> = T extends unknown ? Omit<T, "result"> & { result?: FormInstance[] } : never;

/**
 * The controller response as this hook reads it.
 *
 * SERVER GAP: `taskInspectionAndProgressFormsControllerResponse` declares only
 * `{ status, errorMessage?, result?, count?, lastSaved? }`, but the update path also returns
 * `message`, `addedAttachmentData` and `autoFieldAnswers`, all of which are read below. They are
 * declared here so those reads type-check without `any`; delete this extension once the fields
 * are added to `packages/server/forms/forms.schema.ts`.
 */
type FormsControllerResponseData = WithFormInstanceResult<TaskInspectionAndProgressFormsControllerResponse> & {
  message?: string;
  addedAttachmentData?: { obj?: Record<string, unknown> };
  autoFieldAnswers?: Record<string, unknown>;
};

/**
 * The injected forms controller. Web passes
 * `@Actions/Forms.taskInspectionAndProgressFormsController`, mobile passes its own axios wrapper;
 * both take the request body plus an optional AbortController and resolve to an axios-shaped
 * response.
 */
export type FormsController = (
  payload: Record<string, unknown>,
  abortController?: AbortController,
) => Promise<{ data: FormsControllerResponseData } | void>;

/** What every call site here assumes it got back — see the note on the no-op default. */
type FormsControllerResult = { data: FormsControllerResponseData };

/**
 * One buffered write for the controller's `update` action — literally the `data` it is sent as, so
 * the server schema is the type: `TipfUpdateRequest["data"]`, whose ~50 fields are all `.optional()`
 * because each write path fills a different subset and `{}` is the "nothing pending" sentinel that
 * `setEachAnswer` short-circuits on.
 *
 * This one setter is shared by every preset section (`milestoneContent`, `taskContent`, `WBSContent`,
 * `quantityContent`, `referenceContent`, `reviewContent`, `linkedForm`) as well as this hook's own
 * question-answer paths, and those groups write disjoint sets of `data` fields. A hand-written
 * narrower type therefore cannot be used at the hand-off: it shares no property with the section
 * payloads, so weak-type detection rejects them, and parameter contravariance makes it unfixable on
 * the component side. Exported so the sections declare the same shape instead of `unknown`.
 *
 * `sectionIndex` / `fieldIndex` are the two fields that are NOT part of the wire payload: they are
 * set by the attachments modal (`useFieldsAttachment`) purely so `updateAttachments` can read them
 * back off the buffered write, and the server ignores them.
 */
export type EachAnswerToSave = TipfUpdateRequest["data"] & { sectionIndex?: number; fieldIndex?: number };

/**
 * One row of a table section, taken from the instance's own wire shape rather than restated: the
 * element of `FormInstanceSection["tableDataSource"]`, i.e. `rowId` / `isPreFilledRow` plus one
 * `{ value?, createdDate?, createdBy? }` cell per question `_id` (`tableSectionRow` in
 * `packages/server/forms/inspectionAndProgress.collection.ts`). Deriving it keeps the rows this hook
 * writes from drifting from the ones it reads back off `inspectionData`, and is why `createdBy` has
 * to be tagged with `toObjectIdString` at every write below — the stored cell is a branded id.
 *
 * `NonNullable` drops the `.optional()` on the array itself; the `Exclude` drops the `| undefined`
 * an indexed access carries under `noUncheckedIndexedAccess`, so the row stays writable.
 */
type TableRow = Exclude<NonNullable<FormInstanceSection["tableDataSource"]>[number], undefined>;

/**
 * A `tableDataSource` element as the RENDERERS hand it over.
 * `FormTemplateField.tableDataSource` is `z.array(z.unknown())` on the wire — `forms.schema.ts`
 * models the same row but cannot import it back without a module cycle, and says so — so the rows
 * reached through `FileUploadChangeArgs["formData"]` arrive untyped even though the stored element
 * is the row above. Same guard `useQuestionFields` already uses for this: it asserts only what the
 * reads rely on (the row is a keyed object), and the schema's cube-qa census records all 277 stored
 * template elements and all 1184 form-instance elements as plain objects.
 */
const isTableRow = (row: unknown): row is TableRow => typeof row === "object" && row !== null && !Array.isArray(row);
const readTableRow = (row: unknown): TableRow => (isTableRow(row) ? row : {});

/**
 * The entry list stored in one answer. A table cell's `value` is `z.unknown()` on the wire
 * (`tableSectionCell`) because its shape follows the question's `fieldType`; every reader below sits
 * on a `fileUpload` / `image` question, whose stored value is the uploaded-entry list — the same
 * `FieldFileValue[]` the renderers declare for those types (`RemoveUploadedFileArgs.currentAnswer`).
 *
 * It returns `undefined`, not `[]`, when the value is not a list: a cell that was never answered
 * then still reaches the same `.findIndex` / `.filter` / spread on `undefined` and throws exactly
 * where it threw before. This is a typing change, not a guard.
 */
const readFileList = (value: unknown): FieldFileValue[] | undefined => (Array.isArray(value) ? value : undefined);

/** Same, for a users question, whose stored value is the selected-user list. */
const readUserList = (value: unknown): FieldUserValue[] | undefined => (Array.isArray(value) ? value : undefined);

/** Metadata recorded for an uploaded file alongside its answer value. */
type UploadedFileMeta = { type?: string; name?: string; awsKey?: string };

/**
 * A file being uploaded: a browser `File` on web, or the mobile picker's descriptor, which
 * carries `base64`/`fileName` instead. Stated as one optional-property shape because the two
 * paths meet in `handleFileUploadChange` and are told apart by `mobile`, not by their type.
 */
type UploadCandidate = { type?: string; name?: string; fileName?: string; size?: number; base64?: unknown };

/** The web file-input change event; only `target.files` and `target.value` are touched. */
type FileInputEvent = { target: { files?: FileList | null; value: string } };

/** Comment + signature captured before an approve / reject / roll-back action. */
type FormActionDetails = { comment: string; signDoc: string | null; signType: string };

type CommentDialogState = { status: boolean; source: string; heading: string; btnText: string };

type AddUsersModalState = {
  status: boolean;
  fieldData: OpenAddUserModalArgs["eachField"];
  fieldIndex: OpenAddUserModalArgs["fieldIndex"] | undefined;
  sectionIndex: OpenAddUserModalArgs["sectionIndex"] | undefined;
  section: OpenAddUserModalArgs["section"];
  source: string;
  tableRowIndex: number | undefined;
  uniqueId: string;
  showCrossIcon?: boolean;
};

type WarningModalState = { status: boolean; source?: string; data?: FormInstanceSection };

export type TableSectionConfig = {
  height: string;
  expandMode: boolean;
  section: FormInstanceSection;
  sectionIndex: number;
};

type ExportModalState = { state: boolean; paperSize: string; tableFormat: string };

type UrlModalState = FieldModalStateArgs & { answerValue?: unknown; uniqueId?: string };

type GeoLocationModalState = FieldModalStateArgs & { uniqueId?: string };

interface UseFormsProps {
  navigate?: (to: string) => void;
  params?: Readonly<Record<string, string | undefined>>;
  /** Read by the caller only; accepted here so the existing call sites keep compiling. */
  scheduleId?: string | null;
  contractId?: string | null;
  resourceId?: string;
  taskInspectionAndProgressFormsController?: FormsController;
  /**
   * Optional because `FilesSlickGrid` constructs the hook with only `navigate`. Every read below
   * asserts it (`userDetails!`) rather than guarding: the assertion is erased at emit, so a caller
   * that omits it still throws exactly where it threw before.
   */
  userDetails?: RootState["userDetails"];
  portalData?: RootState["portalData"];
  projectData?: RootState["projectData"];
  getCurrencyData?: typeof import("@Actions/Currency").getCurrencyData;
  getContractDetailUrl?: (props: {
    selectedTab?: string;
    projectId?: string;
    contractId?: string;
    versionId?: string;
  }) => string;
  getFormsListUrl?: (props: { selectedTab?: string; projectId?: string; resourceType?: string }) => string;
  uuid?: () => string;
  /** Passed by the web caller for parity with the other injected services; not read here. */
  downloadFile?: typeof import("@Services/fileDownload").downloadFile;
  mobile?: boolean;
  navigation?: { replace: (screen: string, params: Record<string, unknown>) => void };
  bottomSheetRefFiles?: { current?: { present: () => void } | null };
  bottomSheetRefUsers?: { current?: { present: () => void } | null };
  bottomSheetRefAddedUsers?: { current?: { present: () => void } | null };
  projectId?: string;
}
const useForms = ({
  navigate,
  params,
  contractId,
  resourceId,
  // KNOWN BUG, preserved verbatim: the default resolves to `undefined`, so a caller that omits this
  // prop and then reaches a controller call throws on `res.data`. `formPreview.tsx` relies on never
  // reaching one. `FormsController` therefore says the call may resolve to nothing, and each read
  // below asserts `FormsControllerResult` — erased at emit, so the throw is unchanged. The real fix
  // is to make the prop required, which changes the hook's contract.
  taskInspectionAndProgressFormsController = async () => {},
  userDetails,
  portalData,
  projectData,
  getCurrencyData,
  getContractDetailUrl,
  getFormsListUrl,
  uuid,
  mobile = false,
  navigation,
  bottomSheetRefFiles,
  bottomSheetRefUsers,
  bottomSheetRefAddedUsers,
  projectId,
}: UseFormsProps) => {
  const { t } = useTranslation();
  const presetSectionsObject = getPresetSectionsObject();
  const dispatch = useDispatch();
  const abortControllerList = useRef<Record<string, AbortController[]>>({});
  const { RESOURCE_MODIFY } = folderPermissions();
  const customModules = useSelector((state: RootState) => state.customModules);
  const [levelEditModal, setLevelEditModal] = useState<{
    open: boolean;
    levelIndex: number | null;
    /** The level the dynamic-user-config modal produced, posted as-is in the submit payload. */
    editedLevel?: FormInstanceFlowLevel;
    isValid?: boolean;
  }>({ open: false, levelIndex: null });
  const selectSignType = (args: string) => {
    const signType = args;
    if (!userDetails!.signature && signType == SignTypeValues.IMPORT_PROFILE_SIGNATURE) {
      dispatch(
        showToastNotification({
          content: t("Profile sign not set") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      setFormActionDetails((prev: FormActionDetails) => ({
        ...prev,
        signType,
        signDoc: null,
      }));
    }
  };

  // The instance is fetched on mount; until then the state is the empty object the original code
  // started from. Everything downstream gates on `inspectionData._id`, so the assertion (erased at
  // emit) records that gate instead of widening every read to `| undefined`.
  const [inspectionData, setInspectionData] = useState<FormInstance>({} as FormInstance);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentModalOpenedFor, setAttachmentModalOpenedFor] = useState<{
    sectionIndex?: number;
    fieldIndex?: number;
  }>({});
  const [sideBarTaskDetails, setSideBarTaskDetails] = useState<unknown[]>([]);
  const [eachAnswerToSave, setEachAnswerToSave] = useState<EachAnswerToSave>({});
  const [answerSaved, setAnswerSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null | undefined>(null);
  const [isFileUploading, setIsFileUploading] = useState<Array<{ type?: string; uniqueId: string }>>([]);
  // Tracks in-flight multi-file uploads per field so concurrent uploads to the same
  // field append to a shared, up-to-date answer array instead of each starting from the
  // answer value captured before its own (awaited) upload — which clobbers earlier files.
  // The entry only lives while uploads are in flight (refcounted), so once all settle the
  // next interaction reads the freshly committed answer again.
  const inFlightUploadsRef = useRef<Record<string, { count: number; value: unknown[] }>>({});
  const [openCommentDialog, setOpenCommentDialog] = useState<CommentDialogState>({
    status: false,
    source: "",
    heading: "",
    btnText: "",
  });
  const [formActionDetails, setFormActionDetails] = useState<FormActionDetails>({
    comment: "",
    signDoc: null,
    signType: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
  });
  const [accordionExpandedState, setAccordionExpandedState] = useState<string[]>([]);
  const [showSideBar, setShowSideBar] = useState(true);
  // `section` / `fieldData` / the indices exist only once the modal is opened for a field; the
  // closed state is `{ status: false }`. Asserted (erased) rather than declared optional so the
  // consumers that read them off the open modal keep the shape they already rely on.
  const [showAddUsersModal, setShowAddUsersModal] = useState<AddUsersModalState>({
    status: false,
  } as AddUsersModalState);
  const [showWarningModal, setShowWarningModal] = useState<WarningModalState>({ status: false });
  const [modifySection, setModifySection] = useState<string[]>([]);
  const [actionBtnDisabled, setActionBtnDisabled] = useState(false);
  // `section` / `sectionIndex` are filled in only when a table section is expanded; same erased
  // assertion as above so `index.tsx` can keep reading `tableSectionConfig.section` directly.
  const [tableSectionConfig, setTableSectionConfig] = useState<TableSectionConfig>({
    height: "max-h-60",
    expandMode: false,
  } as TableSectionConfig);
  const [showWaitSpinner, setShowWaitSpinner] = useState(false);
  const formValidator = useFormValidator({});

  // teams and location states for mobile
  const [teamData, setTeamData] = useState<unknown>(null);
  const [loactionData, setLoactionData] = useState<unknown>(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [imageFieldSelectedIndex, setImageFieldSelectedIndex] = useState<number | null>(null);
  const [openExportModal, setOpenExportModal] = useState<ExportModalState>({
    state: false,
    paperSize: "A4",
    tableFormat: "card",
  });
  // Both modals carry the field they were opened for; the closed state is just `{ state: false }`.
  const [showUrlModal, setShowUrlModal] = useState<UrlModalState>({ state: false } as UrlModalState);
  const [showGeoLocationModal, setShowGeoLocationModal] = useState<GeoLocationModalState>({
    state: false,
  } as GeoLocationModalState);

  /** Set by the consumer that renders the form element; only read back through the returned ref. */
  const formObject = useRef<unknown>(null);

  const commentSaveDisabledState =
    openCommentDialog?.source !== "rollBack" &&
    (formActionDetails.comment.trim() == "" ||
      (formActionDetails.signType === SignTypeValues.ATTACH_SIGNED_DOCUMENT && formActionDetails.signDoc == null));

  const ActionItems = inspectionData._id
    ? [
        ...(inspectionData.isSubmitted
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.approveBtnTxt}`,
                id: "approve",
                icon: "TickMark",
                disabled: (() => {
                  const allowedUserIdsToApprove = inspectionData.formFlow[
                    inspectionData.currentLevelOfForm
                  ]!.allowedUsersToApprove?.map((user) => user.id);
                  // KNOWN BUG, preserved verbatim: `allowedUsersToApprove` is `.optional()` on the
                  // wire and `?.map` propagates that, but — unlike the submit / roll-back / reject
                  // branches — this one has no `|| []`, so a level without the list throws while
                  // rendering the action menu. Assertion erased at emit; the fix is the `|| []` its
                  // siblings already have, which changes behaviour.
                  const approveBtnIsDisabled = !(
                    allowedUserIdsToApprove!.includes(userDetails!.id as ObjectIdString) && inspectionData.isSubmitted
                  );
                  return approveBtnIsDisabled;
                })(),
              },
            ]
          : [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.submitBtnTxt}`,
                id: "submit",
                icon: "TickMark",
                disabled: (() => {
                  const allowedUserIdsToSubmit =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToSubmit?.map(
                      (user) => user.id,
                    ) || [];
                  const submitBtnIsDisabled =
                    // KNOWN BUG, preserved verbatim: `isArchived` is `.optional()` on the wire but is
                    // dereferenced unconditionally, so an instance stored without it throws here.
                    !allowedUserIdsToSubmit.includes(userDetails!.id as ObjectIdString) &&
                    !inspectionData.isArchived!.status;
                  return submitBtnIsDisabled;
                })(),
              },
            ]),
        ...(Object.hasOwn(inspectionData.formFlow[inspectionData.currentLevelOfForm]!, "allowedUsersToRollBack")
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.rollBackBtnTxt}`,
                id: "rollBack",
                icon: "RollBackIcon",
                disabled: (() => {
                  const allowedUserIdsToRollBack =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToRollBack?.map(
                      (user) => user.id,
                    ) || [];

                  const rollBackBtnIsDisabled = !allowedUserIdsToRollBack.includes(userDetails!.id as ObjectIdString);
                  return rollBackBtnIsDisabled;
                })(),
              },
            ]
          : []),
        ...(Object.hasOwn(inspectionData.formFlow[inspectionData.currentLevelOfForm]!, "allowedUsersToReject")
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.rejectBtnTxt}`,
                id: "reject",
                icon: "Reject",
                disabled: (() => {
                  const allowedUserIdsToReject =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToReject?.map(
                      (user) => user.id,
                    ) || [];

                  const rejectBtnIsDisabled = !allowedUserIdsToReject.includes(userDetails!.id as ObjectIdString);
                  return rejectBtnIsDisabled;
                })(),
              },
            ]
          : []),
      ]
    : [];

  const sideBarAccordions = [
    ...(contractId
      ? [
          {
            type: "contractDetails",
            heading: t("Contract Details"),
            iconName: "details",
          },
        ]
      : []),
    { type: "details", heading: t("Details"), iconName: "details" },
    { type: "formFlow", heading: t("Form Flow"), iconName: "activity" },
    { type: "access", heading: t("Access"), iconName: "access" },
    { type: "issues", heading: t("Issues"), iconName: "IssuesIcon" },
  ];

  const fetchFormData = async () => {
    const res = (await taskInspectionAndProgressFormsController({ resourceId })) as FormsControllerResult;
    // Same pre-fetch sentinel as `inspectionData`: `{}` when the request fails, and every caller
    // gates on `data._id` before reading anything else.
    let data: FormInstance = {} as FormInstance;
    if (_.get(res, "data.status", false)) {
      // KNOWN BUG, preserved verbatim: `result` is `.optional()` on the response schema but is
      // indexed unconditionally, so a `status: true` response without it throws. The `!` after the
      // index is note-1 bookkeeping and is NOT a claim the element is there: an empty `result` still
      // leaves `data` undefined, which is what the `if (data)` below (and `data._id` in
      // `setInitialData`) already deals with.
      data = res!.data.result![0]!;
      if (data) {
        let modifiedData = {
          ...data,
        };
        if (portalData?.frontendUrl?.includes("rustomjee") && data.resourceType == formCategories.bills) {
          const template = data.selectedForm.template?.map((section) => {
            const allQuestions = section.questionFields?.map((question) => {
              if (question.fieldLabel == "MBOOK_NO" && data.MBOOK_NO) {
                question.answer = {
                  value: data.MBOOK_NO,
                };
              }
              if (question.fieldLabel == "SES_NO" && data.SES_NO) {
                question.answer = {
                  value: data.SES_NO,
                };
              }
              return question;
            });
            section.questionFields = allQuestions;
            return section;
          });

          modifiedData = {
            ...data,
            selectedForm: {
              ...data.selectedForm,
              template,
            },
          };
        }
        setInspectionData(() => ({ ...modifiedData }));
      }
    }
    setIsDataFetched(true);
    return data;
  };
  const setInitialData = async () => {
    const data = await fetchFormData();
    if (data._id) {
      axiosWrapper!.post<RecentAccessResponse>(`${getBackendUrl()}/recentAccess/recentAccess`, {
        module: data.resourceType,
        metaData: {
          _id: resourceId,
          name: data.title,
          projectId: data.projectId,
          resourceType: data.resourceType,
        },
        projectId: data.projectId,
        platform: mobile ? PLATFORM.mobile : PLATFORM.web,
      });
      if (!mobile) {
        const allSectionsInCurrentLevel: Array<string> = [];
        data.formFlow[data.currentLevelOfForm]!.dataSource!.map((data) => {
          data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id!));
        });
        handleExpandSelectedSections(allSectionsInCurrentLevel);
      }
    }
    if (data.contractId) {
      dispatch(getContractDetails({ contractId: data.contractId }));
      dispatch(getCurrencyData!({ projectId: params!.projectId as ObjectIdString }));
    }
  };
  useEffect(() => {
    setInitialData();
  }, []);

  async function getLocationForMobile() {
    const teamRawResponse = await axiosWrapper!.post<LocationControllerResponse>(
      `${getBackendUrl()}/projects/locationController`,
      {
        value: { params: { projectId }, projectId },
      },
    );
    let teamResponse = teamRawResponse.data.result;
    return teamResponse;
  }

  async function getTeamDataForMobile() {
    const locationRawResponse = await axiosWrapper!.post<TeamsControllerResponse>(
      `${getBackendUrl()}/projects/teamsController`,
      {
        value: { params: { projectId }, projectId },
      },
    );
    let locationResponse = locationRawResponse.data.result;
    return locationResponse;
  }

  const handleChangeAnswer = ({
    source,
    answerValue,
    fieldIndex,
    sectionIndex,
    tableRowIndex,
    questionId,
  }: {
    source?: string;
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    tableRowIndex?: number;
    questionId?: string;
  }) => {
    // Mark the form-data update as a non-urgent transition so the browser can paint
    // the typed character before React re-renders the entire form tree. Without this,
    // every keystroke synchronously re-renders all sections/fields and delays paint.
    startTransition(() => {
      if (source == formSectionType.FORM) {
        setInspectionData((prev) => {
          const modifiedTemplate = [...prev.selectedForm.template];
          const questionField = modifiedTemplate![sectionIndex!]?.questionFields![fieldIndex!];
          if (questionField) {
            questionField.answer = questionField.answer
              ? { ...questionField.answer, value: answerValue }
              : { value: answerValue };
          }
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      } else {
        setInspectionData((prev) => {
          const modifiedTemplate = [...prev.selectedForm.template];
          const section = modifiedTemplate![sectionIndex!];
          if (section) {
            section.tableDataSource = section.tableDataSource!.map((row: TableRow, index: number) => {
              if (index == tableRowIndex) {
                row[questionId!] = {
                  createdDate: Date.now(),
                  createdBy: toObjectIdString(userDetails!._id),
                  value: answerValue,
                };
              }
              return row;
            });
          }
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      }
    });
  };

  /**
   * `args` is the option toggle (`{ checked, value }`) in LIST view and the already-selected value
   * list in DROPDOWN view; the two are told apart by `viewType`, not by their type, so it is taken
   * as `unknown` and narrowed with erased casts at each read.
   */
  function getMultiSelectValue(args: unknown, currentAnswer: { value?: unknown[] } | undefined, viewType: string) {
    if (viewType === optionsViewType.LIST) {
      const prevAns = currentAnswer?.value || [];
      return (args as { checked?: boolean }).checked
        ? [...prevAns, (args as { value?: unknown }).value]
        : prevAns.filter((ans) => ans !== (args as { value?: unknown }).value);
    }

    if (viewType === optionsViewType.DROPDOWN) {
      return args || [];
    }

    return [];
  }

  const handleMobile = ({
    args,
    answerValue,
    sectionIndex,
    eachField,
    section,
    source,
    tableRowIndex,
    fieldIndex,
  }: {
    /** The raw platform value for this field type — see the switch below. */
    args: unknown;
    source: string;
    answerValue: unknown;
    fieldIndex: number;
    sectionIndex: number;
    tableRowIndex: number;
    eachField: QuestionField;
    section: FormInstanceSection;
  }) => {
    const { _id, fieldType, viewType = optionsViewType.LIST } = eachField;
    // The answer's shape follows `fieldType`; only the multi-select path reads it, and there it is
    // always a list, so it is stated as one here rather than re-widened at the call.
    let currentAnswer = (eachField.answer ?? {}) as { value?: unknown[] };

    if (source === formSectionType.TABLE_SECTION) {
      // KNOWN BUG, preserved verbatim: `tableDataSource` is `.optional()` on the wire but the
      // optional chain starts one level too late, so a table section without it throws instead of
      // falling through to `{}`. Assertion erased at emit.
      currentAnswer = (section.tableDataSource![tableRowIndex]?.[_id] ?? {}) as { value?: unknown[] };
    }

    switch (fieldType) {
      case "single-line":
      case "multi-line":
      case "single-select":
        answerValue = args;
        break;

      case "multi-select":
        answerValue = getMultiSelectValue(args, currentAnswer, viewType);
        break;

      case "date-time":
        answerValue = (args as Date).getTime();
        break;

      case "look-up":
        answerValue = args;
        break;

      case "number":
        answerValue = Number((args as string).replace(/\D/g, ""));
        break;

      case "geoLocation":
        answerValue = args;
        break;

      default:
        break;
    }

    handleChangeAnswer({
      source,
      answerValue,
      fieldIndex,
      sectionIndex,
      tableRowIndex,
      questionId: _id,
    });

    return answerValue;
  };

  const handleInputChange = async ({
    args,
    answerValue,
    sectionIndex,
    eachField,
    section,
    source = "",
    tableRowIndex,
    fieldIndex,
  }: HandleInputChangeArgs) => {
    // See `HandleInputChangeArgs`: the renderer's payload is the widest shape its three call sites
    // share, so the Zod shapes this body needs are asserted back in — the pattern `questionFields.tsx`
    // describes for its own two hand-off points. All erased at emit, so an absent `eachField` still
    // throws on the destructure exactly where it threw before.
    const { _id } = eachField as QuestionField;

    const questionId = _id;
    const sectionId = section!._id;
    if (mobile) {
      answerValue = handleMobile({
        args,
        answerValue,
        sectionIndex: sectionIndex!,
        eachField: eachField as QuestionField,
        section: section as FormInstanceSection,
        source: source!,
        tableRowIndex: tableRowIndex!,
        fieldIndex: fieldIndex!,
      });
    }
    if (!([undefined, NaN] as unknown[]).includes(answerValue)) {
      if (source == formSectionType.FORM) {
        setEachAnswerToSave({
          sectionId,
          questionId,
          answer: { value: answerValue },
        });
      } else {
        const modifiedTemplate = _.cloneDeep(inspectionData.selectedForm.template);
        const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == tableRowIndex) {
              row[_id] = {
                createdDate: Date.now(),
                createdBy: toObjectIdString(userDetails!._id),
                value: answerValue,
              };
            }
            return row;
          },
        );
        setEachAnswerToSave({
          tableSectionId: sectionId,
          tableSectionDataSource: modifiedTableDataSource,
        });
      }
    }
  };

  const fileToBase64 = async (file: File) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => {
        console.log(e);
      };
    });

  const validateMaxFiles = (eachField: QuestionField, prevAns: unknown[]) => {
    if (eachField.maxNoOfFiles && prevAns && prevAns.length >= eachField.maxNoOfFiles) {
      dispatch(
        showToastNotification({
          content: t("Max only {{count}} files can be uploaded", { count: eachField.maxNoOfFiles }) as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }
    return true;
  };

  const validateFileType = (file: UploadCandidate, eachField: QuestionField, allowedFileTypes: string[]) => {
    if (
      (eachField.fieldType === "image" && !allowedFileTypes.includes(file.type!)) ||
      (eachField.fieldType === "fileUpload" && allowedFileTypes.length && !allowedFileTypes.includes(file.type!))
    ) {
      dispatch(
        showToastNotification({
          content: t("File type not allowed") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }
    return true;
  };

  const validateFileSize = (file: UploadCandidate, e: unknown) => {
    const maxFileSizeInBytes = 10 * 1024 * 1024;
    if (file.size! > maxFileSizeInBytes) {
      const msg = t("File size exceeds the limit. Max file size allowed is {{max, number}} MB", { max: 10 });
      if (mobile) {
        dispatch(showToastNotification({ content: msg as TranslatedString, type: TOAST_TYPE_WARNING }));
      } else {
        alert(msg);
        (e as FileInputEvent).target.value = "";
      }
      return false;
    }
    return true;
  };

  const updateFormTemplate = (args: FileUploadChangeArgs, fileMeta: UploadedFileMeta, fileBase64Str: unknown) => {
    const { formData, sectionIndex, fieldIndex, sectionId, eachField, tableRowIndex, source } = args;
    const questionId = eachField._id;
    // KNOWN BUG, preserved verbatim: `formData` defaults to `{}` in `handleFileUploadChange`, and
    // `questionFields` / `tableDataSource` are `.optional()` on the wire, yet every step below is
    // dereferenced unconditionally — an upload against a section that lacks them throws and the
    // file is silently not attached. The assertions are erased at emit.
    const modifiedTemplate = _.cloneDeep(formData!.selectedForm!.template!);
    let answerValue: unknown[] = [{ ...fileMeta }];

    if (source === formSectionType.FORM) {
      if (eachField.answer?.value) {
        answerValue = [...(eachField.answer.value as unknown[]), fileMeta];
      }
      modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = { value: answerValue };

      return {
        template: modifiedTemplate,
        data: {
          addFileUploadFieldData: { fileBase64Str, awsKey: fileMeta.awsKey },
          answer: { value: answerValue },
          sectionId,
          questionId,
        },
      };
    }

    // `formData` here is the renderer's `RendererFormData`, whose rows are `unknown` (see
    // `readTableRow`), so the row and the cell's list are read through the guards rather than
    // indexed blind. Behaviour note: the original chain had no `?.` between the row index and the
    // question id, so a row that is not there threw; it now reads as an empty row and the upload
    // proceeds with just this file. `tableRowIndex` is the index of the row being rendered, so that
    // branch is not reachable from the UI.
    const prevAns = readFileList(
      readTableRow(formData!.selectedForm!.template![sectionIndex!]!.tableDataSource![tableRowIndex])[questionId]
        ?.value,
    );
    if (prevAns) answerValue = [...prevAns, fileMeta];

    modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
      (row: unknown, i: number) =>
        i === tableRowIndex
          ? {
              ...readTableRow(row),
              [questionId]: { createdBy: userDetails!._id, createdDate: Date.now(), value: answerValue },
            }
          : row,
    );

    return {
      template: modifiedTemplate,
      data: {
        tableSectionId: sectionId,
        tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        addFileUploadFieldData: { fileBase64Str, awsKey: fileMeta.awsKey },
      },
    };
  };

  const base64ToFile = (base64EncodedImg: string) => {
    const [meta = "", base64Data = ""] = base64EncodedImg.split(",");
    const mimeType = meta.match(/data:(.*?);base64/)?.[1] || "image/jpeg";
    const byteString = atob(base64Data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    const extension = mimeType.split("/")[1] || "jpeg";
    return new File([byteArray], `${uuid!()}.${extension}`, { type: mimeType });
  };

  const handleFileUploadInTemplate = async (file: File | string) => {
    const fileToUpload = typeof file === "string" && file.startsWith("data:") ? base64ToFile(file) : (file as File);

    const formDataForFile = new FormData();
    formDataForFile.append("uploadFiles", fileToUpload);
    const response = await axiosWrapper.post<{ status: boolean; uuid?: string; errorMessage?: string }>(
      `${getBackendUrl()}/forms/uploadFilesInTemplate`,
      formDataForFile,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    if (!response?.data?.status || !response.data.uuid) return;
    return response.data.uuid;
  };
  const handleFileUploadChange = async (args: FileUploadChangeArgs) => {
    const {
      e,
      eachField,
      sectionIndex,
      tableRowIndex,
      source,
      tableRowId = null,
      // Same `{}` sentinel as `inspectionData`; see `updateFormTemplate` for what it costs.
      formData = {} as FormInstance,
      handleCustomFieldData,
      fieldIndex,
    } = args;

    const questionId = eachField._id;
    const allowedFileTypes = eachField.allowedFileTypes || [];
    let prevAns = (eachField.answer?.value as unknown[]) || [];

    if (source === formSectionType.TABLE_SECTION) {
      // Same untyped renderer rows as `updateFormTemplate`; the guards reproduce this chain exactly
      // — a missing row, a missing cell or a cell with no list all end at the `|| []` they already
      // ended at.
      prevAns =
        readFileList(
          readTableRow(formData.selectedForm!.template![sectionIndex!]!.tableDataSource![tableRowIndex])[questionId]
            ?.value,
        ) || [];
    }

    if (!validateMaxFiles(eachField, prevAns)) return;
    const file = (mobile ? e : (e as FileInputEvent).target.files?.[0]) as UploadCandidate | undefined;
    if (!file) return;
    if (!validateFileType(file, eachField, allowedFileTypes)) return;
    if (!validateFileSize(file, e)) return;

    let fileBase64Str = file.base64;
    if (!mobile) {
      if (!(file instanceof File)) {
        dispatch(
          showToastNotification({
            content: t("Invalid file") as TranslatedString,
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }
      fileBase64Str = await fileToBase64(file);
    }
    const awsKey = uuid!();
    const fileMeta = { type: file.type, name: file.name || (file as UploadCandidate).fileName, awsKey };

    const { template, data } = updateFormTemplate(args, fileMeta, fileBase64Str);

    const uniqueId = tableRowId ? `${tableRowId}${questionId}` : questionId;

    setIsFileUploading((prev) => [...prev, { type: eachField.fieldType, uniqueId }]);

    // Seed/refcount the shared in-flight accumulator for this field so that a second
    // upload that starts before this one resolves appends to the same array.
    const existingInFlight = inFlightUploadsRef.current[uniqueId];
    if (existingInFlight) {
      existingInFlight.count += 1;
    } else {
      inFlightUploadsRef.current[uniqueId] = { count: 1, value: prevAns };
    }

    try {
      if (handleCustomFieldData) {
        // On mobile `file` is the picker's descriptor rather than a `File`; the upload helper
        // already casts internally, so the same value is forwarded here.
        const UUID = await handleFileUploadInTemplate(file as File);
        if (UUID) {
          // Append to the current (possibly already-grown) value at apply time, not to the
          // stale prevAns captured before the upload, so concurrent uploads don't clobber.
          const current = inFlightUploadsRef.current[uniqueId];
          const base = current ? current.value : prevAns;
          const answerValue = [...base, { ...fileMeta, awsKey: UUID }];
          if (current) current.value = answerValue;
          (handleCustomFieldData as HandleCustomFieldData)({
            eachField,
            fieldIndex,
            answerValue,
            sectionIndex,
            tableRowIndex,
            source,
          });
        }
      } else if (inspectionData._id) {
        const res = (await taskInspectionAndProgressFormsController({
          action: "update",
          _id: resourceId,
          data,
        })) as FormsControllerResult;
        if (res.data.status) {
          setAnswerSaved(true);
          if (!mobile) setTimeout(() => setAnswerSaved(false), 500);
          dispatch(
            showToastNotification({ content: t("File uploaded") as TranslatedString, type: TOAST_TYPE_SUCCESS }),
          );
          // KNOWN BUG, preserved verbatim: the controller returns `lastSaved` as a string, but on the
          // instance document `lastSaved` is `{ timeStamp, userId }`. Writing the response value here
          // replaces an object with a string, so any later `inspectionData.lastSaved.timeStamp` read
          // yields `undefined`. Cast erased at emit; the fix belongs on the server response shape.
          setInspectionData(
            (prev) =>
              ({
                ...prev,
                lastSaved: res.data.lastSaved,
                selectedForm: { ...prev.selectedForm, template },
              }) as FormInstance,
          );
        } else {
          dispatch(showToastNotification({ content: untranslated(res.data.errorMessage), type: TOAST_TYPE_ERROR }));
        }
      }
    } finally {
      // Always clear the per-field spinner, even if the upload/save throws (5xx/network),
      // and release the in-flight accumulator once the last concurrent upload settles.
      const current = inFlightUploadsRef.current[uniqueId];
      if (current) {
        current.count -= 1;
        if (current.count <= 0) delete inFlightUploadsRef.current[uniqueId];
      }
      setIsFileUploading((prev) => prev.filter((d) => d.uniqueId !== uniqueId));
    }
  };

  const saveEditedImage = async ({
    base64EncodedImg,
    awsKey,
    handleCustomFieldData,
    sectionId,
    questionId,
    eachField,
    fieldIndex,
    sectionIndex,
    tableRowIndex,
    source,
    imageIndex,
    currentAnswer,
  }: SaveEditedImageArgs) => {
    if (handleCustomFieldData) {
      const UUID = await handleFileUploadInTemplate(base64EncodedImg);
      if (UUID) {
        // In a table section the answer lives per-row, not on eachField.answer — use the
        // already-resolved currentAnswer. Build a new array instead of mutating in place.
        const updateAns = (currentAnswer?.value ?? []).map((image, index: number) =>
          index === imageIndex ? { ...image, awsKey: UUID } : image,
        );
        (handleCustomFieldData as HandleCustomFieldData)({
          eachField,
          fieldIndex,
          answerValue: updateAns,
          sectionIndex,
          tableRowIndex,
          source,
        });
      }
    } else if (inspectionData._id) {
      setEachAnswerToSave({
        // KNOWN BUG, preserved verbatim: `awsKey` is `string | undefined` on the renderer's
        // `SaveEditedImageArgs`, but the server declares `addFileUploadFieldData.awsKey` required —
        // so editing an image that has no awsKey yet sends `undefined` and the request fails Zod
        // validation server-side rather than being skipped here. Asserted (erased at emit) to keep
        // that exact request; the fix is to skip the write when `awsKey` is absent.
        addFileUploadFieldData: { fileBase64Str: base64EncodedImg, awsKey: awsKey! },
        sectionId,
        questionId,
      });
    }
  };

  const handleRemoveUploadedFile = ({
    fileData,
    sectionIndex,
    fieldIndex,
    sectionId,
    questionId,
    source,
    tableRowIndex,
    eachField,
    handleCustomFieldData,
    currentAnswer,
  }: RemoveUploadedFileArgs) => {
    const { awsKey, fileVersion } = fileData;
    const matchingValue = awsKey || fileVersion;
    const matchingKey = awsKey ? "awsKey" : "fileVersion";

    if (handleCustomFieldData) {
      // `currentAnswer` is already declared as this question's file list
      // (`RemoveUploadedFileArgs`), so the element is named for what it is — the old `TableRow`
      // annotation here described a table ROW, not the file entry being filtered.
      const modifiedAnswer = (currentAnswer?.value ?? []).filter(
        (data: FieldFileValue) => data[matchingKey] != matchingValue,
      );
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField,
        fieldIndex,
        answerValue: modifiedAnswer,
        sectionIndex,
        tableRowIndex,
        source,
      });
    } else if (inspectionData._id) {
      const modifiedTemplate = [...inspectionData.selectedForm.template];
      if (source == formSectionType.FORM) {
        // KNOWN BUG, preserved verbatim: the optional chain stops at `answer`, so a question whose
        // answer exists with no `value` throws rather than being skipped. Assertions erased at emit.
        const fileIndex = (
          eachField as { answer?: { value?: Array<Record<string, unknown>> } }
        ).answer?.value!.findIndex((file) => file.awsKey === awsKey);
        const modifiedAnswer = (
          eachField as { answer?: { value?: Array<Record<string, unknown>> } }
        ).answer?.value!.filter((data) => data[matchingKey] != matchingValue);

        modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
          ...modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer,
          value: modifiedAnswer,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        const payload: EachAnswerToSave = {
          answer: { value: modifiedAnswer },
          sectionId,
          questionId,
        };
        if (awsKey) {
          // KNOWN BUG, preserved verbatim: `fileIndex` above is `number | undefined` (the `?.` stops
          // at `answer`), but the server declares `removeFileUploadFieldData.fileIndex` required — so
          // a field whose `answer` is absent sends `undefined` and the request fails Zod validation
          // server-side. Asserted (erased at emit); the fix is to skip the write when there is no
          // index to remove.
          payload.removeFileUploadFieldData = { fileIndex: fileIndex! };
        }
        setEachAnswerToSave(payload);
      } else {
        // Both reads were the same cell, so it is read once. The `!`s are note-1 bookkeeping
        // (`noUncheckedIndexedAccess`) and are erased: a missing row or a never-answered cell still
        // throws on this line, and a cell whose `value` is not a list still throws on the
        // `.findIndex` below — exactly where each threw before.
        const storedFiles = readFileList(
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex]![questionId]!.value,
        );
        const fileIndex = storedFiles!.findIndex((file) => file.awsKey === awsKey);
        const modifiedAnswer = storedFiles!.filter((data) => data[matchingKey] != matchingValue);

        modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == tableRowIndex) {
              row[questionId] = {
                createdBy: toObjectIdString(userDetails!._id),
                createdDate: Date.now(),
                value: modifiedAnswer,
              };
            }
            return row;
          },
        );

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        const payload: EachAnswerToSave = {
          tableSectionId: sectionId,
          questionId,
          tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        };
        if (awsKey) {
          payload.removeFileUploadFieldData = { tableRowIndex, fileIndex };
        }
        setEachAnswerToSave(payload);
      }
    }
  };

  const manageAbortControllers = (questionId: string | undefined, abortController: AbortController) => {
    if (!questionId) return;

    const existingControllers = _.get(abortControllerList, `current.${questionId}`) as AbortController[] | undefined;
    if (existingControllers) {
      existingControllers.forEach((c) => c.abort());
      abortControllerList.current[questionId] = [];
    }

    if (abortControllerList.current[questionId]) {
      abortControllerList.current[questionId].push(abortController);
    } else {
      abortControllerList.current[questionId] = [abortController];
    }
  };
  const updateAttachments = (
    res: FormsControllerResult,
    eachAnswerToSave: EachAnswerToSave,
    setInspectionData: React.Dispatch<React.SetStateAction<FormInstance>>,
  ) => {
    if (!res.data.addedAttachmentData && !res.data.autoFieldAnswers) return;

    setInspectionData((prev) => {
      const { addedAttachmentData, autoFieldAnswers, lastSaved } = res.data;

      const { sectionIndex, fieldIndex } = eachAnswerToSave;
      const attachmentObj = addedAttachmentData?.obj;

      const newTemplate = prev.selectedForm.template.map((section: FormInstanceSection, sIdx: number) => {
        if (!section.questionFields) return section;

        let sectionChanged = false;

        const newQuestionFields = section.questionFields!.map((field: QuestionField, fIdx: number) => {
          let updated = field;

          // 1. Attachment addition — only the targeted field.
          if (attachmentObj && sIdx === sectionIndex && fIdx === fieldIndex) {
            updated = {
              ...updated,
              attachmentsData: [...(updated.attachmentsData ?? []), { ...attachmentObj }],
            };
          }

          // 2. Auto-field answer — any field whose _id is in the map.
          if (
            autoFieldAnswers &&
            updated._id != null &&
            Object.prototype.hasOwnProperty.call(autoFieldAnswers, updated._id)
          ) {
            updated = {
              ...updated,
              answer: { ...updated.answer, value: autoFieldAnswers[updated._id] },
            };
          }

          if (updated !== field) sectionChanged = true;
          return updated;
        });

        return sectionChanged ? { ...section, questionFields: newQuestionFields } : section;
      });

      // Same server/document `lastSaved` mismatch as above; `autoFieldAnswers` is also typed more
      // loosely than `answerMap`. One erased cast covers both.
      return {
        ...prev,
        lastSaved,
        answerMap: autoFieldAnswers ? { ...prev.answerMap, ...autoFieldAnswers } : prev.answerMap,
        selectedForm: { ...prev.selectedForm, template: newTemplate },
      } as FormInstance;
    });
  };
  const setEachAnswer = async () => {
    if (!Object.keys(eachAnswerToSave).length) return;

    const abortController = new AbortController();
    const questionId = _.get(eachAnswerToSave, "questionId") || _.get(eachAnswerToSave, "tableSectionId");

    manageAbortControllers(questionId, abortController);

    try {
      const res = (await taskInspectionAndProgressFormsController(
        { action: "update", _id: resourceId, data: eachAnswerToSave },
        abortController,
      )) as FormsControllerResult;

      updateAttachments(res, eachAnswerToSave, setInspectionData);

      if (res.data.status) {
        setAnswerSaved(true);
        setTimeout(() => setAnswerSaved(false), 500);
        setLastSaved(res.data.lastSaved);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      Promise.reject(error);
    }
  };

  useEffect(() => {
    setEachAnswer();
  }, [eachAnswerToSave]);

  useEffect(() => {
    if (inspectionData?._id && isDataFetched) {
      addValidationToForms({
        formValidator,
        inspectionData,
        modifySection,
      });
    }
  }, [inspectionData?._id, inspectionData?.currentLevelOfForm, modifySection, isDataFetched]);

  const openAddUserModal = ({
    eachField,
    fieldIndex,
    sectionIndex,
    section,
    source = formSectionType.FORM,
    tableRowIndex,
    handleCustomFieldData,
    tableRowId,
  }: OpenAddUserModalArgs) => {
    if (eachField.onlyCurrentUser) {
      const currentUser = {
        _id: userDetails!.id,
        signature: userDetails!.signature,
        firstName: userDetails!.firstName,
        fullName: userDetails!.fullName,
        lastName: userDetails!.lastName,
      };
      if (handleCustomFieldData) {
        (handleCustomFieldData as HandleCustomFieldData)({
          eachField,
          answerValue: currentUser,
          fieldIndex,
          sectionIndex,
          tableRowIndex,
          source,
        });
      } else if (inspectionData._id) {
        const modifiedTemplate = [...inspectionData.selectedForm.template];
        if (source == formSectionType.FORM) {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: [currentUser],
          };
          setInspectionData((prev) => ({
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          }));

          setEachAnswerToSave({
            sectionId: section!._id,
            questionId: eachField!._id,
            answer: { value: [currentUser] },
          });
        } else {
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!] = {
            createdBy: toObjectIdString(userDetails!._id),
            createdDate: Date.now(),
            value: [currentUser],
          };
          setInspectionData((prev) => ({
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          }));

          setEachAnswerToSave({
            tableSectionId: section!._id,
            tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
          });
        }
        if (mobile) {
          dispatch(
            showToastNotification({
              content: t("User Added") as TranslatedString,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }
      }
      return;
    }
    setShowAddUsersModal({
      status: true,
      fieldData: { ...eachField },
      fieldIndex,
      sectionIndex,
      section,
      source,
      tableRowIndex,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    });
    bottomSheetRefUsers?.current?.present();
  };

  const closeUsersModal = () => {
    setShowAddUsersModal({ status: false } as AddUsersModalState);
  };

  const removeUserFromList = ({
    fieldIndex,
    sectionIndex,
    user,
    eachField,
    section,
    source = formSectionType.FORM,
    tableRowIndex,
    handleCustomFieldData,
  }: RemoveUserFromListArgs) => {
    if (handleCustomFieldData) {
      // KNOWN BUG, preserved verbatim: `answer` is `.optional()` on the wire but is dereferenced
      // unconditionally, so a users-field that was never answered throws on removal.
      const allUsers = eachField!.answer!.value as Array<{ _id?: string }>;
      const filteredUsers = allUsers.filter((data) => data._id != user._id);
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField,
        answerValue: filteredUsers,
        fieldIndex,
        sectionIndex,
        tableRowIndex,
        source,
      });
    } else if (inspectionData._id) {
      const modifiedTemplate = [...inspectionData.selectedForm.template];
      if (source == formSectionType.FORM) {
        // KNOWN BUG, preserved verbatim: `answer` is `.optional()` on the wire but is dereferenced
        // unconditionally, so a users-field that was never answered throws on removal.
        const allUsers = modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer!.value as Array<
          Record<string, unknown>
        >;
        const filteredUsers = allUsers.filter((data) => data._id != user._id);

        modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
          value: filteredUsers,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          sectionId: section!._id,
          questionId: eachField!._id,
          answer: { value: filteredUsers },
        });

        mobile &&
          setShowAddUsersModal({
            ...showAddUsersModal,
            status: true,
            fieldData: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!,
            fieldIndex,
            sectionIndex,
            section: section as AddUsersModalState["section"],
            source,
            tableRowIndex,
          });
      } else {
        // `!`s are note-1 bookkeeping and erased: a missing row or a never-answered cell still
        // throws on this line, and a cell whose `value` is not the user list still throws on the
        // `.filter` below — the same two throws, in the same order, as before.
        const allUsers = readUserList(
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!]!.value,
        );
        const filteredUsers = allUsers!.filter((data) => data._id != user._id);

        modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!] = {
          createdBy: toObjectIdString(userDetails!._id),
          createdDate: Date.now(),
          value: filteredUsers,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          tableSectionId: section!._id,
          tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        });
        mobile &&
          setShowAddUsersModal({
            ...showAddUsersModal,
            status: true,
            fieldData: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!,
            fieldIndex,
            sectionIndex,
            section: section as AddUsersModalState["section"],
            source,
            tableRowIndex,
          });
      }
    }
  };

  const openAddedUserModalInMobile = ({
    eachField,
    fieldIndex,
    sectionIndex,
    showCrossIcon = false,
    section,
    source,
    tableRowIndex,
  }: {
    eachField: QuestionField;
    fieldIndex: number;
    sectionIndex: number;
    showCrossIcon: boolean;
    section: FormInstanceSection;
    source: string;
    tableRowIndex: number;
  }) => {
    // `uniqueId` is only set by `openAddUserModal`; this mobile entry point never carried it.
    setShowAddUsersModal({
      status: true,
      fieldData: { ...eachField },
      fieldIndex,
      sectionIndex,
      showCrossIcon,
      section,
      source,
      tableRowIndex,
    } as AddUsersModalState);
    bottomSheetRefAddedUsers?.current?.present();
  };

  const verifySection = (sectionIds: string[]) => {
    let uniqueSectionIds: Set<string> | string[] = new Set([...sectionIds, ...inspectionData.sectionsVerified]);
    uniqueSectionIds = Array.from(uniqueSectionIds);
    setInspectionData((prev) => ({
      ...prev,
      sectionsVerified: uniqueSectionIds,
    }));
    setEachAnswerToSave({ sectionsVerified: [...uniqueSectionIds] });
  };

  function handleAttachmentClick(sectionIndex: number, fieldIndex: number) {
    setShowAttachmentModal(true);
    setAttachmentModalOpenedFor({
      sectionIndex,
      fieldIndex,
    });
    bottomSheetRefFiles?.current?.present();
  }

  const closeAttachmentModal = () => {
    setShowAttachmentModal(false);
  };

  const handleModifyCancel = (event: { stopPropagation: () => void } | undefined, sectionId: string) => {
    event?.stopPropagation();
    setModifySection((prev) => prev.filter((id) => id != sectionId));
  };

  const verifyToggleStatus = (section: FormInstanceSection) => {
    // KNOWN BUG, preserved verbatim: a section's per-level `permissions` map is `.optional()` on the
    // wire but is indexed unconditionally, so a section authored without it throws while rendering
    // the verify toggle. Assertions erased at emit.
    // `.id` is `.optional()` on `userDetailsSchema` because the slice starts as `{}` before sign-in;
    // this hook only runs inside an authenticated route, and every other level-action check in this
    // file reads it the same way. Erased at emit, so `includes(undefined)` — which is what a missing
    // id produced before — is still what a missing id produces.
    const allowedUsersToVerifyPostSub = section.permissions![inspectionData.currentLevelOfForm]!.Verify;
    return !(allowedUsersToVerifyPostSub.includes(userDetails!.id!) && inspectionData.isSubmitted);
  };

  const saveInspectionData = async (type: string) => {
    const data: Record<string, unknown> = {};
    if (type == "submit") {
      const { editedLevel, levelIndex } = levelEditModal;
      if (editedLevel && levelIndex !== null) {
        data.editedLevel = editedLevel;
      }
      if (inspectionData.currentLevelOfForm == inspectionData.formFlow.length - 2) {
        data.isSubmitted = true;
      } else {
        data.isSubmitted = false;
        data.proceedToNextLevel = true;
      }
    } else if (type == "approve") {
      data.isApproved = true;
    } else if (type == "reject") {
      data.isRejected = true;
    } else if (type == "rollback") {
      data.rollBack = true;
    }
    if (["approve", "reject", "rollback"].includes(type)) {
      data.actionComment = formActionDetails.comment;
      data.actionSignDoc = formActionDetails.signDoc;
      data.actionSignType = formActionDetails.signType;
    }
    setActionBtnDisabled(true);
    const res = (await taskInspectionAndProgressFormsController({
      action: "update",
      _id: resourceId,
      data,
    })) as FormsControllerResult;
    setActionBtnDisabled(false);
    return res;
  };

  const handleRollBack = async () => {
    setShowWaitSpinner(true);
    setActionBtnDisabled(true);
    const res = await saveInspectionData("rollback");
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Form Rolled back to previous level"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      const updatedFormFLow = [...inspectionData.formFlow];
      updatedFormFLow[inspectionData.currentLevelOfForm - 1]!.actionStatus = false;
      updatedFormFLow[inspectionData.currentLevelOfForm - 1]!.actionDetails = {};
      await fetchFormData();
      closeCommentDialog();
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      setShowWaitSpinner(false);
    }
    setActionBtnDisabled(false);
  };

  const handleFormActionClick = (args: { item: { id?: string } }) => {
    if (args.item.id == "approve") {
      if (inspectionData.sectionsVerified?.length != inspectionData.selectedForm.template.length) {
        dispatch(
          showToastNotification({
            content: t("Please verify all sections before approving the form") as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        setOpenCommentDialog({ status: true, heading: "Approve", btnText: "Approve", source: "approve" });
      }
    }
    if (args.item.id == "reject") {
      setOpenCommentDialog({ status: true, heading: "Reject", btnText: "Reject", source: "reject" });
    }
    if (args.item.id == "submit") {
      handleSubmitClick();
    }
    if (args.item.id == "rollBack") {
      setOpenCommentDialog({ status: true, heading: "Roll Back", btnText: "Roll Back", source: "rollBack" });
    }
  };

  const beforeOpenActionDropdown = (args: { items?: Array<{ id?: string; disabled?: boolean }> }) => {
    const items = args?.items;
    if (!items?.length) return;

    const currentFlow = inspectionData.formFlow?.[inspectionData.currentLevelOfForm];
    if (!currentFlow) return;

    const findItem = (id: string) => items.find((it: { id?: string; disabled?: boolean }) => it?.id === id);
    const allowedIds = (key: string) =>
      ((currentFlow[key] as Array<{ id: string }> | undefined)?.map((user) => user.id) ?? []) as string[];

    const rollBackItem = findItem("rollBack");
    if (rollBackItem && Object.hasOwn(currentFlow, "allowedUsersToRollBack")) {
      rollBackItem.disabled = !allowedIds("allowedUsersToRollBack").includes(userDetails!.id as string);
    }

    const rejectItem = findItem("reject");
    if (rejectItem && Object.hasOwn(currentFlow, "allowedUsersToReject")) {
      rejectItem.disabled = !allowedIds("allowedUsersToReject").includes(userDetails!.id as string);
    }

    if (inspectionData.isSubmitted) {
      const approveItem = findItem("approve");
      if (approveItem) {
        approveItem.disabled = !(
          allowedIds("allowedUsersToApprove").includes(userDetails!.id as string) && inspectionData.isSubmitted
        );
      }
    } else {
      const submitItem = findItem("submit");
      if (submitItem) {
        submitItem.disabled =
          !allowedIds("allowedUsersToSubmit").includes(userDetails!.id as string) && !inspectionData.isArchived?.status;
      }
    }
  };

  const handleFormStateChange = async () => {
    const res = (await taskInspectionAndProgressFormsController({
      action: "archive",
      resourceIds: [inspectionData._id],
      data: { isArchived: { status: true } },
    })) as FormsControllerResult;

    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Successfully Archived") as TranslatedString,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    let urlLink;
    if (inspectionData?.contractId) {
      urlLink = getContractDetailUrl!({
        selectedTab: formCategoryObject[inspectionData.resourceType]?.selectedTab ?? "list",
        projectId: params!.projectId,
        contractId: inspectionData?.contractId,
      });
    } else {
      urlLink = getFormsListUrl!({
        selectedTab: "list",
        projectId: params!.projectId,
        resourceType: inspectionData.resourceType,
      });
    }
    navigate!(urlLink);
  };

  const handleCollapseAll = () => {
    setAccordionExpandedState([]);
  };
  const handleExpandAll = (template: FormInstanceSection[] = []) => {
    const sectionIds: Array<string> = template?.map((section) => section._id!);
    setAccordionExpandedState([...sectionIds]);
  };

  const handleExpandSelectedSections = (sectionIds: Array<string>) => {
    setAccordionExpandedState((prev: Array<string>) => [...prev, ...sectionIds]);
  };

  const handleAccStateChange = (args: unknown, sectionId: string) => {
    setAccordionExpandedState((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id != sectionId);
      }

      return [...prev, sectionId];
    });
  };

  const showToastAndRedirect = (content: TranslatedString) => {
    dispatch(
      showToastNotification({
        content,
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    if (inspectionData?.contractId) {
      if (!mobile) {
        const urlLink = getContractDetailUrl!({
          selectedTab: formCategoryObject[inspectionData.resourceType]?.selectedTab ?? "list",
          projectId: params!.projectId,
          contractId: inspectionData?.contractId,
        });
        navigate!(urlLink);
      } else {
        navigation!.replace("FormsList", {
          formList: inspectionData.resourceType,
          projectId: params!.projectId,
          contractId: inspectionData?.contractId,
        });
      }
    } else if (!mobile) {
      const urlLink = getFormsListUrl!({
        selectedTab: "list",
        projectId: params!.projectId,
        resourceType: inspectionData.resourceType,
      });
      navigate!(urlLink);
    } else {
      navigation!.replace("FormsList", {
        formList: inspectionData.resourceType,
        projectId: params!.projectId,
      });
    }
  };

  const handleFormSubmission = async () => {
    const { isAllMandatoryFieldsFilled, errorToastContent } = checkMandatoryFields(inspectionData);
    if (isAllMandatoryFieldsFilled) {
      const res = await saveInspectionData("submit");
      if (res.data.status) {
        await fetchFormData();
        dispatch(
          showToastNotification({
            content: t("Form submitted successfully navigate back to see full list") as TranslatedString,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(errorToastContent),
          type: TOAST_TYPE_ERROR,
        }),
      );
      formValidator.validateAll();
    }
  };
  const handleSubmitClick = async () => {
    if (
      inspectionData.currentLevelOfForm < inspectionData.formFlow.length - 1 &&
      inspectionData.formFlow[inspectionData.currentLevelOfForm + 1]!.allowDynamicUserConfig
    ) {
      setLevelEditModal(() => ({
        open: true,
        levelIndex: inspectionData.currentLevelOfForm + 1,
      }));
    } else {
      await handleFormSubmission();
    }
  };

  const closeCommentDialog = () => {
    // KNOWN BUG, preserved verbatim: this writes `false` into a state declared as the dialog
    // object, so every later `openCommentDialog.source` / `.status` read resolves to `undefined`
    // instead of the closed-dialog shape. Cast erased at emit; the fix is to reset to
    // `{ status: false, source: "", heading: "", btnText: "" }`, which changes what re-renders.
    setOpenCommentDialog(false as never);
    setShowWaitSpinner(false);
    setFormActionDetails({
      comment: "",
      signDoc: null,
      signType: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
    });
  };

  const handleApproveClick = async () => {
    if (inspectionData.sectionsVerified?.length != inspectionData.selectedForm.template.length) {
      dispatch(
        showToastNotification({
          content: t("Please verify all sections before approving the form") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      setShowWaitSpinner(true);
      const res = await saveInspectionData("approve");
      if (res.data.status) {
        showToastAndRedirect("Form Approved Successfully" as TranslatedString);
        setShowWaitSpinner(false);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        setShowWaitSpinner(false);
      }
    }
  };

  function verifyAllState() {
    let isVerifyAllDisabled = true;
    (_.get(inspectionData, "selectedForm.template", []) as FormInstanceSection[]).forEach((section) => {
      const secPermission = getSectionPermission({
        section,
        inspectionData,
        userId: userDetails!.id,
        type: "Verify",
      });
      if (secPermission && inspectionData.sectionsVerified.length < inspectionData.selectedForm.template.length) {
        isVerifyAllDisabled = false;
      }
    });
    return isVerifyAllDisabled;
  }

  const handleSaveCmntClick = async () => {
    if (openCommentDialog.source == "reject") {
      setShowWaitSpinner(true);
      const res = await saveInspectionData("reject");
      if (res.data.status) {
        showToastAndRedirect(res.data.message as TranslatedString);
        setShowWaitSpinner(false);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        setShowWaitSpinner(false);
      }
    } else if (openCommentDialog.source == "approve") {
      handleApproveClick();
    } else if (openCommentDialog.source == "rollBack") {
      handleRollBack();
    }
  };

  function onClickWarningModalConfirm({
    source,
    showWarningModal,
  }: {
    source: string;
    showWarningModal: WarningModalState;
  }) {
    if (source == "verify") {
      verifySection([showWarningModal.data!._id!]);
    }
    if (source == "modify") {
      setModifySection((prev) => [...prev, showWarningModal.data!._id!]);
    }
    if (source == "verifyAll") {
      const tempSectionIds: string[] = [];
      inspectionData.selectedForm.template?.map((section) => {
        const permission = getSectionPermission({
          section,
          inspectionData,
          userId: userDetails!.id,
          type: "Verify",
        });
        if (permission) {
          tempSectionIds.push(section._id!);
        }
      });
      verifySection([...tempSectionIds]);
    }
    hideWarningModal();
  }

  // `section` is absent for the "verifyAll" source, which resolves its own section list in
  // `onClickWarningModalConfirm` and never reads `showWarningModal.data`. `null` is accepted
  // alongside `undefined` because the mobile caller (packages/mobile Forms.tsx) passes `null`.
  const handleShowWarningModal = (
    event: { stopPropagation: () => void } | undefined,
    source: string,
    section?: FormInstanceSection | null,
  ) => {
    event?.stopPropagation();
    setShowWarningModal({ status: true, source, data: section ?? undefined });
  };
  const hideWarningModal = () => {
    setShowWarningModal({ status: false });
  };

  const handleExportPdf = async () => {
    const formData = {
      formId: inspectionData._id,
      projectId: inspectionData.project._id,
      paperSize: openExportModal.paperSize,
      tableSectionFormat: openExportModal.tableFormat,
    };
    setOpenExportModal({ state: false, paperSize: "A4", tableFormat: "card" });
    exportPdf({
      formId: formData.formId,
      projectId: formData.projectId,
      paperSize: formData.paperSize,
      tableSectionFormat: formData.tableSectionFormat,
      exportType: EXPORT_TYPE.PDF,
    });
  };

  const handleAddTableRowClick = ({
    section,
    sectionIndex,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
  }) => {
    // Built empty then filled, rather than as one literal: `TableRow` carries a string index
    // signature whose value type is the answer object, so a fresh literal containing `rowId` is
    // checked against that signature and rejected. Same object, same two writes, at runtime.
    const newObjRow: TableRow = {};
    newObjRow.rowId = uuid!();
    inspectionData.selectedForm.template[sectionIndex!]!.questionFields!.map((field) => {
      newObjRow[field._id] = {};
    });
    const modifiedTemplate = [...inspectionData.selectedForm.template];

    // KNOWN BUG, preserved verbatim: `tableDataSource` is `.optional()` on the wire but is spread
    // unconditionally, so adding a row to a table section that has none throws.
    modifiedTemplate![sectionIndex!]!.tableDataSource = [
      ...modifiedTemplate[sectionIndex!]!.tableDataSource!,
      newObjRow,
    ];

    setInspectionData((prev) => ({
      ...prev,
      selectedForm: {
        ...prev.selectedForm,
        template: modifiedTemplate,
      },
    }));

    setEachAnswerToSave({
      tableSectionId: section!._id,
      tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
    });
  };

  const handleDeleteTableRow = ({
    section,
    sectionIndex,
    rowIndex,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
    rowIndex: number;
  }) => {
    const modifiedTemplate = [...inspectionData.selectedForm.template];

    // KNOWN BUG, preserved verbatim: `tableDataSource` is `.optional()` on the wire but is spliced
    // unconditionally, so a table section without it throws when a row is deleted.
    const dataSource = modifiedTemplate![sectionIndex!]!.tableDataSource;

    dataSource!.splice(rowIndex, 1);

    modifiedTemplate![sectionIndex!]!.tableDataSource = [...dataSource!];

    setInspectionData((prev) => ({
      ...prev,
      selectedForm: {
        ...prev.selectedForm,
        template: modifiedTemplate,
      },
    }));

    setEachAnswerToSave({
      tableSectionId: section!._id,
      tableSectionDataSource: [...dataSource!],
    });
  };

  function handleAddUserClick({
    preSelectedUsers,
    handleCustomFieldData,
  }: {
    preSelectedUsers: unknown[];
    handleCustomFieldData?: unknown;
  }) {
    const selectedUsers = [...preSelectedUsers];
    if (inspectionData._id) {
      const sectionIndex = showAddUsersModal.sectionIndex;
      const fieldIndex = showAddUsersModal.fieldIndex;
      if (showAddUsersModal.source == formSectionType.FORM) {
        const modifiedTemplate = [...inspectionData.selectedForm.template];
        if (modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer) {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            ...modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer,
            value: selectedUsers,
          };
        } else {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: selectedUsers,
          };
        }

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          sectionId: modifiedTemplate![sectionIndex!]!._id,
          questionId: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!._id,
          answer: { value: selectedUsers },
        });
      } else {
        const modifiedTemplate = [...inspectionData.selectedForm.template];

        const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == showAddUsersModal.tableRowIndex) {
              row[showAddUsersModal.fieldData._id!] = {
                createdDate: Date.now(),
                createdBy: toObjectIdString(userDetails!._id),
                value: selectedUsers,
              };
            }
            return row;
          },
        );

        modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTableDataSource;

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          tableSectionId: modifiedTemplate![sectionIndex!]!._id,
          tableSectionDataSource: modifiedTableDataSource,
        });
      }
    } else if (handleCustomFieldData) {
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField: showAddUsersModal.fieldData,
        answerValue: selectedUsers,
        fieldIndex: showAddUsersModal.fieldIndex,
        sectionIndex: showAddUsersModal.sectionIndex,
        tableRowIndex: showAddUsersModal.tableRowIndex,
        source: showAddUsersModal.source,
      });
    }
  }
  const handleURLModalState = ({
    state,
    sectionIndex = 0,
    // KNOWN BUG, preserved verbatim: the default is the number `0`, not a field, so a caller that
    // omits `eachField` sets `uniqueId` from `(0)._id` — i.e. `undefined` — and the modal can never
    // match a field. The default is left exactly as it was and the reads assert the field type
    // (erased at emit); the fix is to make `eachField` required.
    eachField = 0 as never,
    tableRowIndex = 0,
    answer = {},
    fieldIndex = 0,
    sectionId,
    source,
    tableRowId,
    // The renderers' own payload type, not a restatement of it — the same contract
    // `handleGeoLocationModalState` below already uses, and what `UrlModalState` is built from.
  }: FieldModalStateArgs) => {
    setShowUrlModal((prev) => ({
      ...prev,
      state,
      sectionIndex,
      eachField,
      tableRowIndex,
      answerValue: answer.value,
      fieldIndex,
      sectionId,
      source,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    }));
  };

  const handleGeoLocationModalState = ({
    sectionIndex,
    eachField,
    tableRowIndex,
    answer,
    fieldIndex,
    sectionId,
    source,
    tableRowId,
    state,
  }: FieldModalStateArgs) => {
    setShowGeoLocationModal({
      sectionIndex,
      eachField,
      tableRowIndex,
      answer,
      fieldIndex,
      sectionId,
      source,
      tableRowId,
      state,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    });
  };

  const handleSaveUrlData = (handleCustomFieldData: unknown, URLData: { displayText?: string; URL?: string }) => {
    if (!URLData.displayText || !URLData.URL) {
      const field = !URLData.displayText ? "Display Text" : "URL";
      dispatch(
        showToastNotification({
          content: t("{{field}} is Missing", { field }) as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    handleURLModalState({ state: false });
    const answerValue = {
      ...URLData,
    };
    const { eachField, sectionIndex, tableRowIndex, fieldIndex, sectionId, source } = showUrlModal;
    // KNOWN BUG, preserved verbatim: see `handleURLModalState` — `eachField` defaults to the number
    // `0`, so this destructure yields `undefined` for `_id` when the modal was opened without a
    // field. Assertion erased at emit.
    const { _id } = eachField!;

    if (handleCustomFieldData) {
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField: showUrlModal.eachField,
        answerValue,
        fieldIndex: showUrlModal.fieldIndex,
        sectionIndex: showUrlModal.sectionIndex,
        tableRowIndex: showUrlModal.tableRowIndex,
        source: showUrlModal.source,
      });
    } else if (inspectionData._id) {
      if (source == formSectionType.TABLE_SECTION) {
        setInspectionData((prev) => {
          const modifiedTemplate = prev.selectedForm.template;
          const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
            (row: TableRow, index: number) => {
              if (index == tableRowIndex) {
                row[_id] = {
                  createdDate: Date.now(),
                  createdBy: toObjectIdString(userDetails!._id),
                  value: answerValue,
                };
              }
              return row;
            },
          );
          modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTableDataSource;

          setEachAnswerToSave({
            tableSectionId: sectionId,
            tableSectionDataSource: modifiedTableDataSource,
          });

          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      } else {
        setInspectionData((prev) => {
          const modifiedTemplate = prev.selectedForm.template;
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: answerValue,
          };
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });

        setEachAnswerToSave({
          sectionId,
          questionId: _id,
          answer: { value: answerValue },
        });
      }
    }
  };

  function checkWhichSectionsExist({
    section,
    sectionIndex,
    contractDetails,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
    contractDetails: { userPermissions?: number[] };
  }) {
    const permission: boolean = getUserPermissionForForm(inspectionData, userDetails!.id, sectionIndex);

    let isCurrentUserAllowed = permission;
    const customModuleSectionHeadings = customModules.map((module) => module.name) || [];
    const customModuleSectionCode = customModules.map((module) => module.resourceType) || [];
    if (inspectionData.contractId) {
      isCurrentUserAllowed = permission && contractDetails.userPermissions?.includes(RESOURCE_MODIFY.id)!;
    }

    return {
      isCurrentUserAllowed,
      doesQuantityExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
      doesTaskExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TASK,
      doesReferenceExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      doesLinkedFormExist:
        [
          ...customModuleSectionHeadings,
          presetSectionsObject.SECTION_TYPE_INSPECTION,
          presetSectionsObject.SECTION_TYPE_BILLS,
          presetSectionsObject.SECTION_TYPE_CHANGE_ORDERS,
          presetSectionsObject.SECTION_TYPE_PROGRESS_UPDATES,
        ].includes(section.sectionHeading) &&
        [
          ...customModuleSectionCode,
          presetSectionsCodeObject.SECTION_TYPE_BILLS,
          presetSectionsCodeObject.SECTION_TYPE_CHANGE_ORDERS,
          presetSectionsCodeObject.SECTION_TYPE_INSPECTION,
          presetSectionsCodeObject.SECTION_TYPE_PROGRESS_UPDATES,
        ].includes(section.sectionCode),
      doesSectionTypeFormsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_FORM,
      doesMilestoneExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_MILESTONE,
      doesWbsItemsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
      doesSummaryOfChangesExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_SUMMARY_OF_CHANGES,
      doesReviewsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
      doesTableSectionExist: section.sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION,
    };
  }

  const getTableSectionShowAnsLogic = (section: FormInstanceSection, field: QuestionField) => {
    const isFinalState = inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status;

    if (isFinalState) return true;

    const hasModifyPermission = getSectionPermission({
      section,
      inspectionData,
      userId: userDetails!.id,
      type: "Modify",
    });

    const isSectionLocked = !hasModifyPermission || !modifySection.includes(section._id!);

    const isPredefinedAllowed =
      !section.allowPreDefineRowValues ||
      (section.allowPreDefineRowValues &&
        (!field.allowPreDefinedColValues || (field.allowPreDefinedColValues && section.allowEditPreDefineRowInForm)));

    return isSectionLocked && !isPredefinedAllowed;
  };

  const getNormalSectionShowAnsLogic = (section: FormInstanceSection, field: QuestionField) => {
    const isFinalState = inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status;

    if (isFinalState) return true;

    const isPredefinedLocked = field.allowPredefinedVal && !field.allowEditingOfPredefinedVal;

    if (isPredefinedLocked) return true;

    const canModifySection =
      getSectionPermission({
        section,
        inspectionData,
        userId: userDetails!.id,
        type: "Modify",
      }) && !modifySection.includes(section._id!);

    return canModifySection;
  };

  return {
    inspectionData,
    isFileUploading,
    setInspectionData,
    showAttachmentModal,
    setShowAttachmentModal,
    attachmentModalOpenedFor,
    setAttachmentModalOpenedFor,
    sideBarTaskDetails,
    setSideBarTaskDetails,
    eachAnswerToSave,
    setEachAnswerToSave,
    answerSaved,
    setAnswerSaved,
    lastSaved,
    setLastSaved,
    openCommentDialog,
    setOpenCommentDialog,
    formActionDetails,
    setFormActionDetails,
    accordionExpandedState,
    setAccordionExpandedState,
    showSideBar,
    setShowSideBar,
    showAddUsersModal,
    setShowAddUsersModal,
    showWarningModal,
    setShowWarningModal,
    modifySection,
    setModifySection,
    sideBarAccordions,
    setInitialData,
    handleInputChange,
    setEachAnswer,
    // dateFieldFocused,
    openAddUserModal,
    closeUsersModal,
    saveInspectionData,
    removeUserFromList,
    verifySection,
    handleAttachmentClick,
    closeAttachmentModal,
    handleModifyCancel,
    verifyToggleStatus,
    handleFormActionClick,
    beforeOpenActionDropdown,
    handleFormStateChange,
    handleCollapseAll,
    handleExpandAll,
    handleAccStateChange,
    showToastAndRedirect,
    handleSubmitClick,
    handleFormSubmission,
    closeCommentDialog,
    handleShowWarningModal,
    hideWarningModal,
    userDetails,
    projectData,
    handleSaveCmntClick,
    teamData,
    setTeamData,
    loactionData,
    setLoactionData,
    setIsDataFetched,
    isDataFetched,
    openAddedUserModalInMobile,
    onClickWarningModalConfirm,
    handleFileUploadChange,
    handleRemoveUploadedFile,
    setTableSectionConfig,
    tableSectionConfig,
    handleExportPdf,
    actionBtnDisabled,
    formObject,
    // addValidationToInputFields,
    handleDeleteTableRow,
    handleAddTableRowClick,
    handleAddUserClick,
    getTeamDataForMobile,
    getLocationForMobile,
    showWaitSpinner,
    selectSignType,
    saveEditedImage,
    verifyAllState,
    imageFieldSelectedIndex,
    setImageFieldSelectedIndex,
    customModules,
    formValidator,
    setOpenExportModal,
    openExportModal,
    handleURLModalState,
    showUrlModal,
    setShowUrlModal,
    handleSaveUrlData,
    handleChangeAnswer,
    ActionItems,
    showGeoLocationModal,
    setShowGeoLocationModal,
    handleGeoLocationModalState,
    checkWhichSectionsExist,
    getTableSectionShowAnsLogic,
    getNormalSectionShowAnsLogic,
    commentSaveDisabledState,
    levelEditModal,
    setLevelEditModal,
  };
};
export default useForms;
